import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useForm, FormProvider } from 'react-hook-form'

import axios from '../../dataClient/dataClient'
import { FlexColumn, HeaderWithButtons, FlexEndContainer, FlexEndButtonErrorContainer, MedBGContainer, Form, FlexRow } from '../styles/Containers'
import { ErrorMessage } from '../common/UserMessage'
import Button, { SecondaryButton } from '../common/Button'
import BodyText, { BoldFormTitle } from '../styles/Text'
import DatabaseFile from '../files/DatabaseFile'
import SelectedAttachment from '../foiAttachments/SelectedAttachment'
import SelectLetterForm from '../letters/SelectLetterForm'
import foiAccessMethod, { ACCESS_METHOD_TYPE } from '../../util/foiAccessMethod'

const TitleContainer = styled(FlexRow)`
  align-items: flex-end;
  height: 37px;
`

const LETTER_NONE_VALUE = ''

const responseIntroMap = {
  editable: 'Your response will be sent with the following letter from drafts:',
  editableEmpty: 'Select a letter from your drafts to send with the response email. Edit to select a draft.',
  editing: 'Letter to include in response email (select from drafts):',
  completed: 'Your response was sent with the following letter:',
  completedEmpty: 'No letter was included in your response.',
  response: 'Your response will be sent with the following letter from drafts:'
}

const physicalResponseIntroMap = {
  editable: 'The following letter will be included in the response package:',
  editableEmpty: 'Select a letter from your drafts to include in your response package. Edit to select a draft.',
  editing: 'Letter to include in response package (select from drafts):',
  completed: 'Your response package included the following letter:',
  completedEmpty: 'No letter was included in your response.',
  response: 'The following letter will be included in the response package:'
}

function ResponseMessage({
  foiId,
  letters = [],
  finalResponseLetterId = null,
  responseAttachment,
  view = 'editable',
  accessMethod = 'DIGITAL',
  onLettersUpdated = () => {}
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [patchError, setPatchError] = useState(false)

  const selectedResponseLetter = useMemo(() => {
    if (finalResponseLetterId === null || finalResponseLetterId === '') return null
    return letters.find(l => l.id === finalResponseLetterId) ?? null
  }, [letters, finalResponseLetterId])

  const selectedDraftLetter = useMemo(() => {
    if (!selectedResponseLetter || selectedResponseLetter.state !== 'DRAFT') return null
    return selectedResponseLetter
  }, [selectedResponseLetter])

  const draftLetters = useMemo(() => letters.filter(l => l.state === 'DRAFT'), [letters])

  const methods = useForm({
    defaultValues: {
      letterId: selectedDraftLetter?.id ?? LETTER_NONE_VALUE
    }
  })
  const { reset, handleSubmit, watch, clearErrors } = methods

  const selectedLetterId = watch('letterId')

  useEffect(() => {
    reset({
      letterId: selectedDraftLetter?.id ?? LETTER_NONE_VALUE
    })
  }, [selectedDraftLetter?.id, reset])

  const toggleEdit = () => setIsEditing(isEditing => !isEditing)

  const onError = (errors, e) => console.error('Error', errors, e)

  function onSubmit({ letterId }) {
    const normalizedId = String(letterId ?? '').trim()
    const isNone = normalizedId === '' || normalizedId === LETTER_NONE_VALUE

    if (isNone && !finalResponseLetterId) {
      reset({ letterId: LETTER_NONE_VALUE })
      setIsEditing(false)
      setPatchError(false)
      return
    }

    setLoading(true)
    setPatchError(false)

    const resetValueTo = isNone ? LETTER_NONE_VALUE : normalizedId

    axios
      .patch(`/foi/${foiId}`, { finalResponseLetterId: isNone ? null : normalizedId }, { headers: { 'Content-Type': 'application/json' } })
      .then(() => {
        reset({ letterId: resetValueTo })
        setIsEditing(false)
        onLettersUpdated()
      })
      .catch(e => {
        setPatchError(true)
        console.error('Failed to update final response letter', e)
      })
      .finally(() => setLoading(false))
  }

  function onCancel() {
    clearErrors('letterId')
    reset({
      letterId: selectedDraftLetter?.id ?? LETTER_NONE_VALUE
    })
    setIsEditing(false)
    setPatchError(false)
  }

  const readOnlyLetterId = view === 'response' || view === 'completed' || !isEditing ? (selectedResponseLetter?.id ?? selectedLetterId) : null

  const displayLetter =
    view === 'response' || view === 'completed' || !isEditing
      ? (selectedResponseLetter ?? (readOnlyLetterId ? draftLetters.find(l => l.id === readOnlyLetterId) : null))
      : null

  const wordingMap = foiAccessMethod.requiresResponsePackage(accessMethod) ? physicalResponseIntroMap : responseIntroMap

  return (
    <>
      {!(view === 'response' && !displayLetter && !responseAttachment) && (
        <FormProvider {...methods}>
          <Form onSubmit={handleSubmit(onSubmit, onError)}>
            {view !== 'response' && (
              <HeaderWithButtons>
                <TitleContainer>
                  <BoldFormTitle>Response</BoldFormTitle>
                </TitleContainer>
                {view === 'editable' &&
                  (!isEditing ? (
                    <Button onClick={toggleEdit}>Edit</Button>
                  ) : (
                    <FlexEndButtonErrorContainer gap="10px">
                      <FlexEndContainer>
                        <SecondaryButton onClick={onCancel} disabled={loading}>
                          Cancel
                        </SecondaryButton>
                        <Button type="submit" loading={loading} disabled={loading}>
                          Save
                        </Button>
                      </FlexEndContainer>
                      {patchError && <ErrorMessage>Error saving response letter selection. Please try again.</ErrorMessage>}
                    </FlexEndButtonErrorContainer>
                  ))}
              </HeaderWithButtons>
            )}

            {view === 'editable' && isEditing ? (
              <SelectLetterForm foiId={foiId} letters={draftLetters} letterLabel={wordingMap['editing']} disabled={loading} />
            ) : (
              <FlexColumn gap="5px">
                <BodyText>{displayLetter ? wordingMap[view] : wordingMap[`${view}Empty`]}</BodyText>
                {displayLetter && <SelectedAttachment foiId={foiId} attachmentType="LETTER" attachment={displayLetter} disabled={loading} />}
              </FlexColumn>
            )}

            {responseAttachment && (
              <FlexColumn gap="5px">
                <BodyText>Other response documents:</BodyText>
                <MedBGContainer padding="5px 30px 5px 15px">
                  <DatabaseFile file={responseAttachment} onDelete={() => {}} spacing="medium" buttonAction="download" />
                </MedBGContainer>
              </FlexColumn>
            )}
          </Form>
        </FormProvider>
      )}
    </>
  )
}

ResponseMessage.propTypes = {
  foiId: PropTypes.string.isRequired,
  letters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      state: PropTypes.oneOf(['DRAFT', 'SENT']).isRequired
    })
  ),
  finalResponseLetterId: PropTypes.string,
  responseAttachment: PropTypes.object,
  view: PropTypes.oneOf(['editable', 'completed', 'response']),
  accessMethod: PropTypes.oneOf(Object.values(ACCESS_METHOD_TYPE)),
  onLettersUpdated: PropTypes.func
}

export default ResponseMessage
